import { IAssemblyApplicationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { AssemblyParameterDisplay } from 'pages/Settings/AssemblyType/AssemblyParameterDisplay';
import { EditAssemblyParameterModal } from 'pages/Settings/AssemblyType/EditAssemblyParameterModal';
import { AssemblyParameterElementDto } from 'pages/Settings/AssemblyType/parameter_form/types';
import { useState } from 'react';
import styled from 'styled-components';
import { deleteAssemblyParameter } from 'utils/assembly_application';

const ParametersContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

export type AssemblyParameterDetailTreeProps = Readonly<{
    application: IAssemblyApplicationResponse;
    expandedParameterCodes: string[];
    onParameterExpand: (parameterCode: string) => void;
    onChange: () => void;
}>;

export function AssemblyParameterDetailTree({
    application,
    expandedParameterCodes,
    onParameterExpand,
    onChange,
}: AssemblyParameterDetailTreeProps) {
    const parameters = application.ParametersDefinition?.Data
        ? (Object.values(
              application.ParametersDefinition.Data
          ) as AssemblyParameterElementDto[])
        : [];

    const { showToast } = useNotificationContext();

    const [parameterToEdit, setParameterToEdit] = useState<AssemblyParameterElementDto>();
    const [
        parameterToDelete,
        setParameterToDelete,
    ] = useState<AssemblyParameterElementDto>();

    const { mutate: deleteParameter, isPending: isDeleting } = useMutation({
        mutationFn: async (parameterCode: string) =>
            deleteAssemblyParameter(application.Id, parameterCode),

        onSuccess: () => {
            setParameterToDelete(undefined);
            onChange();
            showToast({
                type: 'success',
                content: 'Successfully deleted assembly application parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to delete assembly application parameter',
            }),
    });

    const onEditParameterClose = (saved: boolean) => {
        setParameterToEdit(undefined);
        if (saved) {
            onChange();
        }
    };

    return (
        <>
            {parameters.length === 0 && <div>No Parameters</div>}
            <ParametersContainer>
                {parameters.map((parameter) => (
                    <AssemblyParameterDisplay
                        key={parameter.FieldKey}
                        isExpanded={expandedParameterCodes.includes(parameter.FieldKey)}
                        onExpand={() => onParameterExpand(parameter.FieldKey)}
                        parameter={parameter}
                        onEdit={() => setParameterToEdit(parameter)}
                        onDelete={() => setParameterToDelete(parameter)}
                    />
                ))}
            </ParametersContainer>
            {parameterToEdit && (
                <EditAssemblyParameterModal
                    application={application}
                    parameter={parameterToEdit}
                    onClose={onEditParameterClose}
                />
            )}
            {parameterToDelete && (
                <ConfirmModal
                    title={`Delete '${parameterToDelete.FieldDisplayName}'`}
                    actionText="delete this parameter? Other users in your organization may be referencing it. This parameter value will be erased from any assemblies using it."
                    checkText="delete this parameter"
                    actionButtonText="Delete"
                    closable={!isDeleting}
                    actionable={!isDeleting}
                    onAction={() => deleteParameter(parameterToDelete.FieldKey)}
                    onClose={() => setParameterToDelete(undefined)}
                />
            )}
        </>
    );
}
